/* =========================================================================
   CatholicsApp.Engine — pure logic, no DOM. All money in integer cents.
   Typed port of the prototype's engine — identical semantics. The server is
   authoritative in production; this is the client-side feel engine.

   Value curve (revised):
     [grace 350ms] -> hold $1,000 for 3s -> drain $100/s to $0 (10s)
       -> sit at $0 for 1s -> drain $100/s to floor -$100 (1s) -> hold floor
   ========================================================================= */

export const QUESTIONS_PER_ROUND = 10;
export const START_CENTS = 100_000; // $1,000
export const HARD_START_CENTS = 150_000; // $1,500 — Level-3 (hard-mode) questions pay more
export const DRAIN_PER_SEC = 10_000; // $100 / second
export const FLOOR_CENTS = -10_000; // -$100
export const GRACE_MS = 350;
export const HOLD_FULL_MS = 3000; // hold full value before countdown starts — 2026-08-04
// (Eric: "even on short questions, people cannot read fast enough" — was 2000)
export const DRAIN_ZERO_MS = 10_000; // $1000 -> $0
export const HOLD_ZERO_MS = 1000; // sit at $0
export const TIMEOUT_MS = 16_000; // auto-timeout
export const TIMEOUT_PENALTY = 10_000; // flat -$100
export const WRONG_PENALTY = 10_000; // wrong AFTER the clock hits $0 -> flat -$100 (before $0 -> no penalty)
/** ⭐ Stretch questions are "worth extra, no risk": ×1.5 on the starting value
    (so $1,000 → $1,500, exactly the hard-mode stake) and every negative delta
    clamped to $0. See PLAN-gameplay-v2 §3b. */
export const STRETCH_MULTIPLIER = 1.5;
export const STRETCH_START_CENTS = Math.round(START_CENTS * STRETCH_MULTIPLIER); // $1,500

export interface Question {
  id: number;
  category: string;
  body: string;
  options: string[];
  correctIndex: number;
  citation: string;
  /** Server catalog code (app.Questions.StableCode) when the question was served
      by /api/game/round-questions. Bundled pack questions leave this undefined and
      their code is derived from `id` (data/questionCodes.stableCodeFor). Telemetry,
      /api/log/served and /api/game/answer all key on the code, never on `id`. */
  stableCode?: string;
  /** Starting value override in cents (hard questions: HARD_START_CENTS). Default START_CENTS. */
  valueCents?: number;
  /** Difficulty tier for the round-progression ramp (easyStart.roundPool):
      absent = 1 (easy/popular — always servable), 2 = a little harder (Round 2+),
      3 = heavy (Round 4+). Eric's rule: Round 1 easy ALWAYS; harder as rounds go up. */
  hardness?: 1 | 2 | 3;
  /** ⭐ STRETCH question (PLAN-gameplay-v2 §3b — the hard-mode on-ramp): one
      question a round drawn a tier ABOVE the player's cap, badged, worth ×1.5,
      and RISK-FREE — a wrong answer or a timeout can never cost money. Set by
      the server DTO (`stretch: true`); absent everywhere else. */
  stretch?: boolean;
  /** EXPLICIT extra reading time in seconds (decimal, e.g. 2.1 = +2.1s) added to
      the hold-at-full-value phase before the drain starts. When absent, the
      length presets in readingDelayMs() apply (<80 chars standard · 80–130 +1s ·
      >130 +2s) — Eric's rule: the clock must not outrun the reading. */
  readDelaySec?: number;
}

/** Extra hold-at-full-value time for a question, in ms. An explicit readDelaySec
    override wins; otherwise preset by body length: <80 chars → 0 (standard),
    80–130 → +1s, >130 → +2s. Pure — used by RoundEngine.serveNext. */
export function readingDelayMs(body: string, overrideSec?: number): number {
  if (typeof overrideSec === 'number' && Number.isFinite(overrideSec) && overrideSec >= 0) {
    return Math.round(overrideSec * 1000);
  }
  const len = (body || '').length;
  if (len < 80) return 0;
  if (len <= 130) return 1_000;
  return 2_000;
}

export interface ServedQuestion {
  id: number;
  category: string;
  body: string;
  options: string[];
  correctIndex: number;
  citation: string;
  /** Carried through from Question so the reveal can report the true server code. */
  stableCode?: string;
  /** Carried through so the play screen can badge the ⭐ STRETCH question. */
  stretch?: boolean;
}

export interface AnswerResult {
  wasCorrect: boolean;
  deltaCents: number;
  /** Index into the SHUFFLED options the player saw (what the play screen highlights). */
  correctIndex: number;
  /** Index into the SHUFFLED options the player saw; -1 on timeout. */
  chosenIndex: number;
  /** Index of the chosen option in the question's ORIGINAL option order — the
      order the server's revision (or the bundled pack) stores it in. This is the
      only index the server can score: /api/game/answer compares it against
      QuestionRevisions.CorrectIndex, which knows nothing about the client-side
      shuffle. -1 on timeout. (Live data 2026-09-13: every answer had been
      posted with the shuffled index, so recorded correctness was ~chance.) */
  sourceChosenIndex: number;
  elapsedMs: number;
  citation: string;
  timedOut: boolean;
}

export interface LogEntry {
  qNum: number | string;
  category: string;
  body: string;
  yourAnswer: string;
  correctAnswer: string;
  elapsedMs: number;
  deltaCents: number;
  wasCorrect: boolean;
  timedOut: boolean;
  bonus: boolean;
  round?: string;
  roundIcon?: string;
}

export type EngineState = 'NotStarted' | 'AwaitingAnswer' | 'Revealing';

export function shuffle<T>(arr: readonly T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Authoritative value at elapsed ms since served. The drain RATE is constant
// ($100/s), so a richer start (hard questions) simply drains longer to $0.
// extraHoldMs (reading delay, readingDelayMs()) stretches ONLY the hold phase —
// long questions get their reading time without changing the drain feel.
export function valueAtElapsed(elapsedMs: number, startCents: number = START_CENTS, extraHoldMs = 0): number {
  let t = elapsedMs - GRACE_MS;
  const hold = HOLD_FULL_MS + Math.max(0, extraHoldMs);
  if (t <= 0) return startCents;
  if (t <= hold) return startCents; // phase 1: hold full (+ per-question reading delay)
  t -= hold;
  const drainMs = (startCents / DRAIN_PER_SEC) * 1000; // $1,000→10s · $1,500→15s
  if (t <= drainMs) {
    // phase 2: drain to $0
    return startCents - Math.round((t / 1000) * DRAIN_PER_SEC);
  }
  t -= drainMs;
  if (t <= HOLD_ZERO_MS) return 0; // phase 3: sit at $0
  t -= HOLD_ZERO_MS;
  const v = 0 - Math.round((t / 1000) * DRAIN_PER_SEC); // phase 4: to floor
  return Math.max(FLOOR_CENTS, v);
}

export class RoundEngine {
  pack: Question[];
  n: number;
  order: Question[];
  idx: number;
  bankCents: number;
  state: EngineState;
  current: ServedQuestion | null;
  /** The un-shuffled source of `current` (options in authored/server order). */
  currentSource: Question | null = null;
  servedAt: number;
  breakDone: boolean;
  log: LogEntry[];

  constructor(pack: Question[], questionsPerRound = QUESTIONS_PER_ROUND, initialBankCents = 0) {
    this.pack = pack;
    this.n = Math.min(questionsPerRound, pack.length);
    this.order = shuffle(pack).slice(0, this.n);
    this.idx = -1;
    this.bankCents = initialBankCents;
    this.state = 'NotStarted';
    this.current = null;
    this.servedAt = 0;
    this.breakDone = false;
    this.log = [];
  }

  get questionNumber(): number {
    return this.idx + 1;
  }

  /** Starting value of the current question (hard questions carry a higher one). */
  currentStartCents: number = START_CENTS;

  /** Extra hold-at-full time for the current question (reading delay — set per
      serve from readingDelayMs(); long bodies hold longer before draining). */
  currentExtraHoldMs = 0;

  /** Is the current question a ⭐ stretch question? (×1.5 value, no downside.) */
  currentStretch = false;

  /** Timeout scales with the richer drain runway so hard questions still reach the
      floor phase before auto-timeout (base: $1,000 → 16s; $1,500 → 21s), plus the
      current question's reading delay so long questions keep their full window. */
  get currentTimeoutMs(): number {
    return (
      TIMEOUT_MS +
      Math.max(0, ((this.currentStartCents - START_CENTS) / DRAIN_PER_SEC) * 1000) +
      this.currentExtraHoldMs
    );
  }

  serveNext(now: number): ServedQuestion {
    if (this.idx + 1 >= this.n) throw new Error('Round complete');
    this.idx++;
    const src = this.order[this.idx];
    this.currentSource = src;
    this.currentStretch = src.stretch === true;
    // An explicit valueCents always wins (hard pack / server Level-3 rows); a
    // stretch question without one is worth ×1.5 the standard stake.
    this.currentStartCents = src.valueCents ?? (this.currentStretch ? STRETCH_START_CENTS : START_CENTS);
    this.currentExtraHoldMs = readingDelayMs(src.body, src.readDelaySec);
    const correctText = src.options[src.correctIndex];
    const shuffled = shuffle(src.options);
    const correctIndex = shuffled.indexOf(correctText);
    this.current = {
      id: src.id,
      category: src.category,
      body: src.body,
      options: shuffled,
      correctIndex,
      citation: src.citation,
      stableCode: src.stableCode,
      stretch: this.currentStretch || undefined,
    };
    this.servedAt = now;
    this.state = 'AwaitingAnswer';
    return this.current;
  }

  currentValueCents(now: number): number {
    return valueAtElapsed(now - this.servedAt, this.currentStartCents, this.currentExtraHoldMs);
  }

  /** Map an index into the shuffled options back to the source option order
      (see AnswerResult.sourceChosenIndex). -1 when out of range / no source. */
  sourceIndexOf(shuffledIndex: number): number {
    if (!this.current || !this.currentSource) return -1;
    if (!Number.isInteger(shuffledIndex) || shuffledIndex < 0 || shuffledIndex >= this.current.options.length) return -1;
    return this.currentSource.options.indexOf(this.current.options[shuffledIndex]);
  }

  submitAnswer(chosenIndex: number, now: number): AnswerResult {
    if (this.state !== 'AwaitingAnswer') throw new Error('Not awaiting answer');
    if (!this.current) throw new Error('No current question');
    const elapsedMs = now - this.servedAt;
    const value = valueAtElapsed(elapsedMs, this.currentStartCents, this.currentExtraHoldMs);
    const wasCorrect = chosenIndex === this.current.correctIndex;
    const sourceChosenIndex = this.sourceIndexOf(chosenIndex);
    // Correct: bank the live value, snapped to a whole dollar (never below $0).
    // Wrong before the clock hits $0: no penalty. Wrong at/after $0: flat -$100.
    let delta: number;
    if (wasCorrect) delta = Math.max(0, Math.round(value / 100) * 100); // whole dollars only — no cents
    else delta = value > 0 ? 0 : -WRONG_PENALTY;
    // ⭐ Stretch: "worth extra, NO RISK" — a miss can never cost money.
    if (this.currentStretch && delta < 0) delta = 0;
    this.bankCents += delta;
    this.state = 'Revealing';
    const r: AnswerResult = {
      wasCorrect,
      deltaCents: delta,
      correctIndex: this.current.correctIndex,
      chosenIndex,
      sourceChosenIndex,
      elapsedMs,
      citation: this.current.citation,
      timedOut: false,
    };
    this._record(r);
    return r;
  }

  timeout(now: number): AnswerResult {
    if (this.state !== 'AwaitingAnswer') throw new Error('Not awaiting answer');
    if (!this.current) throw new Error('No current question');
    const elapsedMs = now - this.servedAt;
    // ⭐ Stretch questions never punish — the timeout penalty clamps to $0 too.
    const penalty = this.currentStretch ? 0 : TIMEOUT_PENALTY;
    this.bankCents -= penalty;
    this.state = 'Revealing';
    const r: AnswerResult = {
      wasCorrect: false,
      deltaCents: penalty > 0 ? -penalty : 0, // never -0
      correctIndex: this.current.correctIndex,
      chosenIndex: -1,
      sourceChosenIndex: -1,
      elapsedMs,
      citation: this.current.citation,
      timedOut: true,
    };
    this._record(r);
    return r;
  }

  awardBonus(cents: number, label: string, kind?: string): void {
    this.bankCents += cents;
    this.breakDone = true;
    this.log.push({
      qNum: '★',
      category: kind || 'Bonus',
      body: label,
      yourAnswer: '—',
      correctAnswer: '—',
      elapsedMs: 0,
      deltaCents: cents,
      wasCorrect: cents > 0,
      timedOut: false,
      bonus: true,
    });
  }

  private _record(r: AnswerResult): void {
    const cur = this.current!;
    this.log.push({
      qNum: this.questionNumber,
      category: cur.category,
      body: cur.body,
      yourAnswer: r.chosenIndex >= 0 ? cur.options[r.chosenIndex] : '—',
      correctAnswer: cur.options[cur.correctIndex],
      elapsedMs: r.elapsedMs,
      deltaCents: r.deltaCents,
      wasCorrect: r.wasCorrect,
      timedOut: r.timedOut,
      bonus: false,
    });
  }
}

/* formatting */
export function fmtCents(c: number): string {
  const neg = c < 0;
  const d = Math.abs(c) / 100;
  return (neg ? '−$' : '$') + d.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function fmtDelta(c: number): string {
  return (c >= 0 ? '+$' : '−$') + (Math.abs(c) / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function esc(s: unknown): string {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}
